import { createFileRoute } from '@tanstack/react-router';

import { HStack, VStack } from '@/components/Layout';
import AddStepperCompleteDialog from '@/components/Stepper/AddStepperCompleteDialog';
import Processing from '@/features/import-csv/-components/Processing';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { LIST_EMPTY_DATA } from '@/lib/listPage';

import AddStepperNavigation from '../-components/AddStepperNavigation';
import AddStepperPanelProgress from '../-components/AddStepperPanelProgress';
import AddStepperStepTitleCard from '../-components/AddStepperStepTitleCard';
import BasicForm from '../-components/BasicForm';
import SetPermission from '../-components/SetPermission';
import { PRODUCT_PERMISSION_CREATE_HASH } from '../-constants';
import useBasicForm from '../-hooks/useBasicForm';
import useHash from '../-hooks/useHash';
import useProductPermissionSetPermission from '../-hooks/useProductPermissionSetPermission';
import useCreateProductPermission from '../-mutations/useCreateProductPermission';
import useGetProductLevelAccessCategories from '../-queries/useGetProductLevelAccessCategories';
import { TabKey } from '../../../_list/product-permissions/-constants';
import useGetProductPermissions from '../../../_list/product-permissions/-queries/useGetProductPermissions';

export const Route = createFileRoute(
  '/_staff/settings/permission-and-roles/_form/product-permissions/new/',
)({
  component: RouteComponent,
});

const BASE_LINK = '/settings/permission-and-roles/product-permissions/new';

function RouteComponent() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.settings.permissionAndRoles.createProductPermission',
  });

  const { hash } = useHash();

  const { data: productLevelAccessCategoriesRes } =
    useGetProductLevelAccessCategories();

  const { data: productPermissionsRes } = useGetProductPermissions({
    search: {
      category: TabKey.ALL,
      pageIndex: 0,
      pageSize: 100,
    },
  });

  const { basicFormState, handleChange, isDuplicateName } = useBasicForm({
    data: undefined,
    existingProductPermissions: productPermissionsRes ?? LIST_EMPTY_DATA,
  });

  const {
    setPermissionState,
    checkCategory,
    checkScope,
    handleCategoryChange,
    handleScopeChange,
  } = useProductPermissionSetPermission({
    data: undefined,
    categories: productLevelAccessCategoriesRes,
  });

  const { mutate, isPending, isComplete } = useCreateProductPermission();

  return (
    <HStack className="gap-9 p-10 h-[calc(100vh-64px)]">
      <AddStepperNavigation
        baseLink={BASE_LINK}
        currentHash={hash}
        isEdit={false}
      />

      <VStack className="gap-6 w-full">
        {isPending && <Processing hash={hash} />}

        {!isPending && <AddStepperStepTitleCard hash={hash} />}

        {!isPending && hash === PRODUCT_PERMISSION_CREATE_HASH.FORM && (
          <BasicForm
            basicFormState={basicFormState}
            handleChange={handleChange}
            isDuplicateName={isDuplicateName}
          />
        )}

        {!isPending &&
          hash === PRODUCT_PERMISSION_CREATE_HASH.SET_PERMISSION && (
            <SetPermission
              categories={productLevelAccessCategoriesRes}
              checkCategory={checkCategory}
              checkScope={checkScope}
              handleCategoryChange={handleCategoryChange}
              handleScopeChange={handleScopeChange}
            />
          )}

        {!isPending && (
          <AddStepperPanelProgress
            baseLink={BASE_LINK}
            currentHash={hash}
            basicFormState={basicFormState}
            setPermissionState={setPermissionState}
            mutate={mutate}
            isEdit={false}
            isDuplicateName={isDuplicateName}
          />
        )}

        {isComplete && (
          <AddStepperCompleteDialog
            completeMessage={t(
              'addStepperCompleteDialog.create.completeMessage',
            )}
            completeLinkButtonText={t(
              'addStepperCompleteDialog.create.completeLinkButtonText',
            )}
            completeLinkUrl="/settings/permission-and-roles/product-permissions"
          />
        )}
      </VStack>
    </HStack>
  );
}
